// /home/labdoo/zitech-nexus/frontend/src/pages/admin/UsersList.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, getRoleDisplayName, getRoleBadgeColor } from '../../services/api';
import './UsersList.css';

// SVG Icons
const Icons = {
  Plus: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5V19M5 12H19" />
    </svg>
  ),
  Search: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="17" y2="17" />
    </svg>
  ),
  View: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Edit: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3L21 7L7 21H3V17L17 3Z" />
    </svg>
  ),
  Delete: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7H20" />
      <path d="M10 11V16" />
      <path d="M14 11V16" />
      <path d="M5 7L6 19C6 19.5304 6.21071 20.0391 6.58579 20.4142C6.96086 20.7893 7.46957 21 8 21H16C16.5304 21 17.0391 20.7893 17.4142 20.4142C17.7893 20.0391 18 19.5304 18 19L19 7" />
      <path d="M9 7L10 3H14L15 7" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ul-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  User: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Email: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  Phone: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  Calendar: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  StatusDot: () => (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor">
      <circle cx="4" cy="4" r="4" />
    </svg>
  ),
};

const UsersList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<any[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getAllUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await adminAPI.deleteUser(id);
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Failed to delete user');
      }
    }
  };

  const getRoleColor = (role: string) => {
    switch(role) {
      case 'superadmin': return '#8b5cf6';
      case 'admin': return '#0ea5e9';
      case 'user': return '#10b981';
      case 'trainee': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getRoleBackground = (role: string) => {
    switch(role) {
      case 'superadmin': return '#ede9fe';
      case 'admin': return '#dbeafe';
      case 'user': return '#d1fae5';
      case 'trainee': return '#fef3c7';
      default: return '#f3f4f6';
    }
  };

  const filteredUsers = users.filter(user => 
    user.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    user.email?.toLowerCase().includes(search.toLowerCase())
  );

  const counts = {
    total: users.length,
    active: users.filter(u => u.is_active).length,
    inactive: users.filter(u => !u.is_active).length,
  };

  if (loading) {
    return (
      <div className="ul-loading-container">
        <div className="ul-spinner">
          <Icons.Spinner />
        </div>
        <p className="ul-loading-text">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="ul-container">
      {/* Header */}
      <div className="ul-header">
        <div className="ul-header-left">
          <h1 className="ul-title">User Management</h1>
          <p className="ul-subtitle">Manage system users and their roles</p>
        </div>
        <button 
          className="ul-btn-primary"
          onClick={() => navigate('/dashboard/admin/users/create')}
        >
          <Icons.Plus />
          Add New User
        </button>
      </div>

      {/* Stats Bar */}
      <div className="ul-stats-bar">
        <div className="ul-stat-item">
          <span className="ul-stat-label">Total Users</span>
          <span className="ul-stat-value">{counts.total}</span>
        </div>
        <div className="ul-stat-divider"></div>
        <div className="ul-stat-item">
          <span className="ul-stat-label">Active</span>
          <span className="ul-stat-value ul-stat-active">{counts.active}</span>
        </div>
        <div className="ul-stat-divider"></div>
        <div className="ul-stat-item">
          <span className="ul-stat-label">Inactive</span>
          <span className="ul-stat-value ul-stat-inactive">{counts.inactive}</span>
        </div>
      </div>

      {/* Search */}
      <div className="ul-search">
        <div className="ul-search-wrapper">
          <Icons.Search />
          <input
            type="text"
            className="ul-search-input"
            placeholder="Search users by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="ul-table-wrapper">
        <table className="ul-table">
          <thead>
            <tr>
              <th className="ul-col-name">Name</th>
              <th className="ul-col-email">Email</th>
              <th className="ul-col-phone">Phone</th>
              <th className="ul-col-role">Role</th>
              <th className="ul-col-status">Status</th>
              <th className="ul-col-joined">Joined</th>
              <th className="ul-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={7} className="ul-empty-state">
                  <Icons.Empty />
                  <p className="ul-empty-text">No users found</p>
                  <p className="ul-empty-subtext">Try adjusting your search or create a new user</p>
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id} className="ul-table-row">
                  <td className="ul-col-name">
                    <span className="ul-user-name">
                      <Icons.User />
                      {user.full_name || user.username}
                    </span>
                  </td>
                  <td className="ul-col-email">
                    <a href={`mailto:${user.email}`} className="ul-email-link">
                      <Icons.Email />
                      {user.email}
                    </a>
                  </td>
                  <td className="ul-col-phone">
                    <span className="ul-phone-text">
                      <Icons.Phone />
                      {user.phone_number || '-'}
                    </span>
                  </td>
                  <td className="ul-col-role">
                    <span 
                      className="ul-role-badge"
                      style={{
                        background: getRoleBackground(user.role),
                        color: getRoleColor(user.role)
                      }}
                    >
                      {getRoleDisplayName(user.role)}
                    </span>
                  </td>
                  <td className="ul-col-status">
                    <span 
                      className={`ul-status-badge ${user.is_active ? 'ul-status-active' : 'ul-status-inactive'}`}
                    >
                      <Icons.StatusDot />
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="ul-col-joined">
                    <span className="ul-date-text">
                      <Icons.Calendar />
                      {new Date(user.date_joined).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </td>
                  <td className="ul-col-actions">
                    <div className="ul-actions">
                      <button 
                        className="ul-action-btn ul-action-view"
                        onClick={() => navigate(`/dashboard/admin/users/${user.id}`)}
                        title="View User"
                      >
                        <Icons.View />
                      </button>
                      <button 
                        className="ul-action-btn ul-action-edit"
                        onClick={() => navigate(`/dashboard/admin/users/${user.id}/edit`)}
                        title="Edit User"
                      >
                        <Icons.Edit />
                      </button>
                      <button 
                        className="ul-action-btn ul-action-delete"
                        onClick={() => handleDelete(user.id)}
                        title="Delete User"
                      >
                        <Icons.Delete />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersList;